import { parseDecimal } from "./money"
import type { ItemExtraGroup, MenuItem } from "./types"

/**
 * The figure a unit price is built up from: the promoted base when a rule
 * reaches this dish, the menu price otherwise.
 *
 * It is a separate function rather than a promoted `price`, and that is the
 * whole point. `dishUnitPrice` recomputes base + extras in the browser every
 * time a salsa is ticked, so a promoted figure written into `price` would be
 * silently overwritten on the first selection — and `price` has to keep saying
 * the original, because rule 3 of `Location::DiscountPolicy` is that a discount
 * never mutates the item price and the struck-through figure has to stay
 * readable.
 *
 * Extras are never promoted. A rule takes a percentage off the dish;
 * `Location::PromotionEvaluationService` applies it to the line's base and the
 * extras ride at face value, so adding guacamole to a promoted taco costs the
 * same as adding it to an unpromoted one.
 */
export function dishBasePrice(item: Pick<MenuItem, "price" | "promotion">): string {
  return item.promotion?.promotedPrice ?? item.price
}

/** Price of one unit: base price plus the selected extras across all groups. */
export function dishUnitPrice(
  basePrice: string,
  groups: ItemExtraGroup[],
  selectedIds: Set<number>,
): number {
  const extrasTotal = groups
    .flatMap((group) => group.extras)
    .filter((extra) => selectedIds.has(extra.id))
    .reduce((sum, extra) => sum + parseDecimal(extra.price), 0)

  return parseDecimal(basePrice) + extrasTotal
}

/** True when every group's selection count is within its min/max rules. */
export function requiredGroupsSatisfied(
  groups: ItemExtraGroup[],
  selectedIds: Set<number>,
): boolean {
  return groups.every((group) => {
    const count = group.extras.filter((extra) => selectedIds.has(extra.id)).length
    if (count < group.minSelect) return false
    if (group.maxSelect != null && count > group.maxSelect) return false
    return true
  })
}
